/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Users, Percent, Trash2, Plus, Sparkles } from 'lucide-react';
import { subscribeFamily, unsubscribeFamily, createFamilyCheckout } from '@/lib/pricing';

/**
 * FamilyBundleSection — Sprint 9E: selezione bundle famiglia, editor membri,
 * attivazione (gratuita o via Stripe Checkout) e disattivazione.
 * Extracted from IseeBundlePanel.jsx (Code Quality refactor).
 *
 * @param {object}   tiers      pricing tiers (family_bundles)
 * @param {object}   status     stato pricing corrente (family, combined_discount_pct)
 * @param {function} onRefresh  ricarica stato dal backend
 */
export const FamilyBundleSection = ({ tiers, status, onRefresh }) => {
  const [selectedBundle, setSelectedBundle] = useState(status?.family?.bundle_id || null);
  const [members, setMembers] = useState(status?.family?.members || []);
  const [saving, setSaving] = useState(false);

  // Sync from server state whenever it changes (after refresh)
  useEffect(() => {
    if (status?.family?.members) setMembers(status.family.members);
    if (status?.family?.bundle_id) setSelectedBundle(status.family.bundle_id);
  }, [status]);

  const bundleObj = tiers?.family_bundles?.find((b) => b.id === selectedBundle);
  const maxExtra = Math.max((bundleObj?.members || 1) - 1, 0);
  const isPaidSelected = (bundleObj?.price_monthly || 0) > 0;

  const handleSubscribe = async () => {
    if (!selectedBundle) return toast.error('Seleziona un bundle');
    setSaving(true);
    try {
      const cleaned = members.filter((m) => m.full_name && m.relation).map((m) => ({
        full_name: m.full_name, relation: m.relation, year_of_birth: m.year_of_birth || null,
      }));
      if (isPaidSelected) {
        // Paid bundle → try Stripe Checkout. In whitelabel / no-key mode the
        // backend returns a local_preview URL instead of a real Stripe URL.
        const res = await createFamilyCheckout(selectedBundle, cleaned, window.location.origin);
        if (res?.mode === 'local_preview') {
          toast.info(res.message || 'Modalità dimostrativa: nessun pagamento effettuato.');
        }
        if (res?.url) {
          window.location.href = res.url;
        }
        return;
      }
      await subscribeFamily(selectedBundle, cleaned);
      toast.success('Bundle famiglia attivato');
      await onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleUnsub = async () => {
    try {
      await unsubscribeFamily();
      toast.success('Bundle famiglia rimosso');
      setSelectedBundle(null);
      setMembers([]);
      await onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const addMember = () => setMembers((prev) => [...prev, { _key: `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, full_name: '', relation: '', year_of_birth: '' }]);
  const updateMember = (i, patch) => setMembers((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const removeMember = (i) => setMembers((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <section data-testid="family-bundle-section">
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">Bundle famiglia</h3>
        {status?.family?.bundle_label && (
          <Badge variant="outline" className="ml-auto" data-testid="family-current-bundle">
            {status.family.bundle_label} – {status.family.discount_pct}%
          </Badge>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {tiers?.family_bundles?.map((b) => {
          const active = selectedBundle === b.id;
          return (
            <button key={b.id} type="button" onClick={() => setSelectedBundle(b.id)}
              className={`text-left rounded-2xl border p-3 transition-colors ${active ? 'bg-primary/10 border-primary/50' : 'bg-secondary/30 border-border/60 hover:bg-secondary/50'}`}
              data-testid={`family-bundle-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{b.label}</div>
                <Badge className="bg-primary/15 text-primary border-0">–{b.discount_pct}%</Badge>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{b.description}</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {b.price_monthly > 0 ? `€${b.price_monthly.toFixed(2)}/mese` : 'Gratuito'}
              </div>
            </button>
          );
        })}
      </div>

      {selectedBundle && bundleObj?.members > 1 && (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mt-3 space-y-2" data-testid="family-members-editor">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                Aggiungi fino a {maxExtra} membri aggiuntivi (oltre a te).
              </div>
              <Button variant="outline" size="sm" onClick={addMember} disabled={members.length >= maxExtra}
                data-testid="family-add-member-btn"><Plus className="h-3.5 w-3.5" /> Aggiungi</Button>
            </div>
            {members.map((m, i) => (
              <div key={m._key || `m-existing-${i}`} className="grid grid-cols-[1fr_1fr_90px_auto] gap-2 items-center" data-testid={`family-member-row-${i}`}>
                <Input placeholder="Nome e cognome" value={m.full_name} onChange={(e) => updateMember(i, { full_name: e.target.value })} data-testid={`family-member-name-${i}`} />
                <Input placeholder="Relazione (es. figlio)" value={m.relation} onChange={(e) => updateMember(i, { relation: e.target.value })} data-testid={`family-member-relation-${i}`} />
                <Input type="number" placeholder="Anno" value={m.year_of_birth || ''} onChange={(e) => updateMember(i, { year_of_birth: e.target.value ? parseInt(e.target.value, 10) : '' })} data-testid={`family-member-year-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => removeMember(i)} className="text-destructive hover:text-destructive" data-testid={`family-member-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Button onClick={handleSubscribe} disabled={saving || !selectedBundle} data-testid="family-subscribe-btn">
          <Sparkles className="h-4 w-4" /> {saving ? 'Attendere…' : (isPaidSelected ? 'Attiva con Stripe (ricorrente)' : 'Attiva bundle')}
        </Button>
        {status?.family && (
          <Button variant="outline" onClick={handleUnsub} data-testid="family-unsub-btn">
            <Trash2 className="h-4 w-4" /> Disattiva
          </Button>
        )}
        {status?.combined_discount_pct != null && (
          <Badge className="ml-auto bg-emerald-500/15 text-emerald-600 border-0 gap-1" data-testid="combined-discount-badge">
            <Percent className="h-3 w-3" /> Sconto totale: –{status.combined_discount_pct}%
          </Badge>
        )}
      </div>
    </section>
  );
};
